/*
 * A visit's card that pulls out from the right of the schedule (Details on the visit's popover, owner 2026-11-03): the
 * everyday things done fast without leaving the schedule. Complete or reopen, Reschedule, Team, change the line items,
 * notes, skip or cancel, call or text the client, directions. Client info is changed on the client's page and the rest
 * (checklist, timer, photos, bill part, carry on to another day, apply to future visits) stays on the visit and job pages,
 * one click away at the bottom. Escape, the X or a click outside closes it (not while one of its boxes is open).
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useReopenVisit, useVisit, useVisitStatus } from "../api/hooks";
import { useSession } from "../app/session";
import { DirectionsButton } from "./Directions";
import { dayDate, dayWords, shortDate, timeRange, todayIn } from "./format";
import { Icon } from "./Icon";
import { Menu, useToast } from "./overlays";
import { Banner, Button, IconButton, LoadError, Skeleton, StatusLabel } from "./parts";
import { useVisitSheets } from "./visitSheets";
import { CrewChips, errText, LinesTable, NotesCard, VisitStatus } from "./work";

export function VisitDrawer({ visitRef, onClose }: { visitRef: string; onClose: () => void }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [ref, setRef] = useState(visitRef);
  const visit = useVisit(ref);
  const reopen = useReopenVisit();
  const status = useVisitStatus();
  const v = visit.data;
  const edit = useVisitSheets(v, setRef);
  const today = todayIn(company.timezone);

  useEffect(() => setRef(visitRef), [visitRef]);
  useEffect(() => {
    // A box opened from here (Line items, Team) closes first on Escape; the card stays.
    const key = (e: KeyboardEvent) => e.key === "Escape" && !document.querySelector(".modal") && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  const fail = (e: unknown) => toast(errText(e), { kind: "error" });
  const open = (to: "visit" | "job" | "client") => {
    onClose();
    if (!v) return;
    if (to === "visit") void navigate({ to: "/visits/$ref", params: { ref: v.ref } });
    else if (to === "job") void navigate({ to: "/jobs/$id", params: { id: v.jobId } });
    else void navigate({ to: "/clients/$id", params: { id: v.clientId } });
  };

  let body;
  if (visit.error) body = <LoadError error={visit.error} retry={() => void visit.refetch()} />;
  else if (!v) body = <Skeleton rows={8} />;
  else {
    const editable = can("visit.update");
    const movable = can("schedule.update");
    const done = v.status === "done";
    const off = v.status === "skipped" || v.status === "cancelled";
    const where = v.property?.address ?? v.address;
    const setStatus = (to: "skipped" | "cancelled" | "scheduled", words: string) =>
      status.mutate(
        { ref: v.ref, status: to },
        {
          onSuccess: (d) => {
            setRef(d.ref);
            toast(words, to === "scheduled" ? {} : { undo: () => status.mutate({ ref: d.ref, status: "scheduled" }) });
          },
          onError: fail,
        },
      );
    body = (
      <>
        <div className="vdrawer-top">
          <VisitStatus status={v.status} late={v.date < today} />
          <h2>
            {v.clientName} - {v.jobName}
          </h2>
          <span className="muted">
            {dayWords(v.date, today) === dayDate(v.date) ? dayDate(v.date) : `${dayWords(v.date, today)}, ${shortDate(v.date)}`} ·{" "}
            {timeRange(v.startTime, v.endTime)}
          </span>
          {v.moved ? <span className="muted small">Moved from {shortDate(v.originalDate)}</span> : null}
          <div className="vdrawer-actions">
            {editable ? (
              done ? (
                <Button
                  type="secondary"
                  icon="undo"
                  loading={reopen.isPending}
                  onClick={() =>
                    reopen.mutate(v.ref, {
                      onSuccess: (d) => {
                        setRef(d.ref);
                        toast("Visit reopened");
                      },
                      onError: fail,
                    })
                  }
                >
                  Reopen
                </Button>
              ) : off ? (
                <Button type="secondary" onClick={() => setStatus("scheduled", "Visit is back on")}>
                  Put back on
                </Button>
              ) : (
                <Button icon="check" loading={edit.completing} onClick={edit.markDone}>
                  Complete
                </Button>
              )
            ) : null}
            {movable && !done ? (
              <Button type="secondary" icon="schedule" onClick={() => edit.open("schedule")}>
                Reschedule
              </Button>
            ) : null}
            <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
              {(close) => (
                <>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      open("visit");
                    }}
                  >
                    Open visit page
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      open("job");
                    }}
                  >
                    Open job
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      open("client");
                    }}
                  >
                    Open client
                  </button>
                  {editable && !done && !off ? (
                    <>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          close();
                          setStatus("skipped", `Skipped ${shortDate(v.date)}`);
                        }}
                      >
                        Skip this visit
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="danger"
                        onClick={() => {
                          close();
                          setStatus("cancelled", `Canceled ${shortDate(v.date)}`);
                        }}
                      >
                        Cancel this visit
                      </button>
                    </>
                  ) : null}
                </>
              )}
            </Menu>
          </div>
        </div>
        {v.unconfirmed ? <Banner type="warning">Not confirmed. Waiting on the client to sign the link.</Banner> : null}
        {done && v.completedAt ? (
          <Banner type="success">
            Done {shortDate(v.completedAt.slice(0, 10))}
            {v.completedBy ? ` by ${v.completedBy.replace(/\.$/, "")}` : ""}.
          </Banner>
        ) : null}

        <section className="vdrawer-sec">
          <div className="vdrawer-sec-head">
            <h3>Client</h3>
          </div>
          <Link to="/clients/$id" params={{ id: v.clientId }} onClick={onClose}>
            <b>{v.client.name}</b>
          </Link>
          {v.client.phone ? (
            <a className="vdrawer-fact" href={`tel:${v.client.phone}`}>
              <Icon name="phone" size={16} />
              {v.client.phone}
            </a>
          ) : null}
          {v.client.email ? (
            <a className="vdrawer-fact" href={`mailto:${v.client.email}`}>
              <Icon name="mail" size={16} />
              {v.client.email}
            </a>
          ) : null}
          {where ? (
            <span className="vdrawer-fact">
              <Icon name="pin" size={16} />
              <DirectionsButton address={where} className="link">
                {where}
              </DirectionsButton>
            </span>
          ) : null}
          {v.property?.summary ? <span className="muted small">{v.property.summary}</span> : null}
        </section>

        <section className="vdrawer-sec">
          <div className="vdrawer-sec-head">
            <h3>Team</h3>
            {editable ? (
              <Button type="tertiary" size="small" icon="person" onClick={() => edit.open("team")}>
                Change
              </Button>
            ) : null}
          </div>
          <CrewChips crew={v.crew} />
        </section>

        <section className="vdrawer-sec flush">
          <div className="vdrawer-sec-head">
            <h3>Line items</h3>
            {editable && !done ? (
              <Button type="tertiary" size="small" icon="edit" onClick={() => edit.open("lines")}>
                Change
              </Button>
            ) : null}
          </div>
          {v.lines.length ? (
            <LinesTable lines={v.lines} footer={<span className="muted">This visit</span>} />
          ) : (
            <span className="muted">No line items yet</span>
          )}
          {v.linesEdited ? <span className="muted small">Prices on this visit differ from the job.</span> : null}
        </section>

        {v.officeNotes || v.crewNotes ? (
          <section className="vdrawer-sec">
            <div className="vdrawer-sec-head">
              <h3>Notes from the job</h3>
            </div>
            {v.officeNotes ? (
              <p>
                <StatusLabel status="informative">Office</StatusLabel> {v.officeNotes}
              </p>
            ) : null}
            {v.crewNotes ? (
              <p>
                <StatusLabel status="inactive">Crew</StatusLabel> {v.crewNotes}
              </p>
            ) : null}
          </section>
        ) : null}

        <NotesCard on="visits" id={v.ref} notes={v.notesList} editable={editable} />
        {edit.sheets}
      </>
    );
  }

  return createPortal(
    <>
      <div className="vdrawer-scrim" onClick={onClose} />
      <aside className="vdrawer" role="dialog" aria-label="Visit details">
        <div className="vdrawer-head">
          <span className="muted small">Visit</span>
          <IconButton icon="close" label="Close" onClick={onClose} />
        </div>
        <div className="vdrawer-body">{body}</div>
        {v ? (
          <div className="vdrawer-foot">
            <Button type="secondary" onClick={() => open("job")}>
              Job page
            </Button>
            <Button type="secondary" icon="arrowUpRight" onClick={() => open("visit")}>
              Visit page
            </Button>
          </div>
        ) : null}
      </aside>
    </>,
    document.body,
  );
}
